import Modal from "@/components/Modal/Modal";
import { useAppDispatch } from "@/store/hooks";
import { Box, Button, Divider, Tooltip, Typography } from "@mui/material";
import { getCustomerConfigurationCustomFields, getCustomFields } from '@/store/slices/customerSlice';
import { useSelector } from "react-redux";
import type { RootState } from "@/store/rootReducer";
import { memo, useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import FormControl from "@/components/FormControl/FormControl";
import toast from "@/lib/toast";
import UserService from "@/services/api/user";
import AddressDialog from "@/components/AddressDialog/AddressDialog";
import CustomerService from "@/services/api/customer";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import InputAdornment from "@mui/material/InputAdornment";
import { emailRegex } from "@/lib/regex";
import { useLocationPermission } from "@/hooks/useLocationPermission";
import { extractFileInfo } from "@/lib/methodUtils";
import PhoneNumberInput from "../PhoneNumberInput/PhoneNumberInput";

interface LeadContact {
    id?: string;
    Id?: number;
    Name: string;
    ContactNo: string;
    Email: string;
    Designation: string;
    Location: string;
    IsActive?: boolean;
}

const splitDialCode = (value?: string) => {
    if (!value) return { dial: "+91", number: "" };
    const match = value.trim().match(/^(\+\d{1,4})\s*(.*)$/);
    return match
        ? { dial: match[1], number: match[2]?.replace(/\D/g, "") }
        : { dial: "+91", number: value.replace(/\D/g, "") };
};


const combineDialCode = (dial?: string, number?: string) =>
    number ? `${dial || "+91"} ${number}` : "";

const getKycAdornment = (showStar: boolean, showTick: boolean, points: number) =>
    showStar || showTick ? (
        <InputAdornment position="end">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {showStar && (
                    <Tooltip title={`KYC Score: ${points}%`} arrow>
                        <StarBorderIcon
                            fontSize="small"
                            sx={{ color: "text.disabled", cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                {showTick && (
                    <Tooltip title={`KYC Score: ${points}%`} arrow>
                        <CheckCircleIcon
                            fontSize="small"
                            color="success"
                        />
                    </Tooltip>
                )}
            </Box>
        </InputAdornment>
    ) : null;


const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
            resolve(reader.result?.toString().split(",")[1] || "");
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

interface NewCustomerModalProps {
    open: boolean;
    closeModal: () => void;
    data?: any;
    action: string;
    statusList?: any[];
    sourceList?: any[];
    stageList?: any[];
    typeList?: any[];
    territoryList?: any[];
    referralList?: any[];
    countryList?: any[];
    leadDesignation?: any[];
    crmSettings?: any;
    activeTab?: number;
    onSubmit: (payload: any) => Promise<void>;
}

const NewCustomerModal: React.FC<NewCustomerModalProps> = ({ open, closeModal, data, sourceList, stageList, typeList, territoryList, referralList, countryList, leadDesignation, crmSettings, action, activeTab, onSubmit }) => {
    const dispatch = useAppDispatch();
    const { customerCustomFields, customFields } = useSelector((state: RootState) => state.customer);
    const { customerTags } = useSelector((state: RootState) => state.deal);
    const [selectOptions, setSelectOptions] = useState<Record<string, { label: string; value: any }[]>>({});
    const [openMap, setOpenMap] = useState(false);
    const isEditInitialized = useRef(false);
    const [fileNames, setFileNames] = useState({
        FrontView: "",
        BackView: "",
    });

    const MinMobileNumberLength = crmSettings?.Metadata?.MinMobileLength ?? 7;
    const MaxMobileNumberLength = crmSettings?.Metadata?.MaxMobileLength ?? 15;

    const { checkPermissionAndRun } = useLocationPermission();

    const { control, handleSubmit, reset, watch, setValue, trigger, formState: { isValid, isSubmitting } } = useForm<any>({
        mode: "onChange",
        defaultValues: {
            LeadContacts: [],
            MobileNoCountryCode: "+91",
        }
    });
    const watchedValues = watch();

    const { fields: alternateContacts, append, remove } = useFieldArray<any, "LeadContacts">({
        control,
        name: "LeadContacts"
    });

    const normalize = (v: any) => (v === 0 || v === "" || v === undefined ? null : v);

    const [isFetchingFields, setIsFetchingFields] = useState(false);
    const [customFieldFiles, setCustomFieldFiles] = useState<Record<string, string>>({});
    const [initialCustomFieldFiles, setInitialCustomFieldFiles] = useState<Record<string, string>>({});
    const [removedContacts, setRemovedContacts] = useState<any[]>([]);
    const [removedFiles, setRemovedFiles] = useState({
        RemoveFrontView: false,
        RemoveBackView: false,
    });

    useEffect(() => {
        if (open) {
            setIsFetchingFields(true);
            Promise.all([
                dispatch(getCustomerConfigurationCustomFields()),
                dispatch(getCustomFields())
            ]).finally(() => {
                setIsFetchingFields(false);
            });
        }
    }, [open, dispatch]);

    const handleOpenMap = () => {
        checkPermissionAndRun(() => {
            setOpenMap(true);
        });
    };

    const defaultCustomerInfoFields = [
        { PermanentName: "LeadName", FieldType: "Lead/Customer Name", IsMandatory: true },
        { PermanentName: "RefNo", FieldType: "Ref No." },
        { PermanentName: "ReferredBy", FieldType: "Referred By" },
    ];

    const defaultPrimaryContactFields = [
        { PermanentName: "PersonName", FieldType: "Contact Person Name" },
        { PermanentName: "MobileNo", FieldType: "Mobile No" },
        { PermanentName: "ContactNo", FieldType: "Telephone No" },
        { PermanentName: "Email", FieldType: "Email" },
        { PermanentName: "PersonDesignation", FieldType: "Contact Person Designation" },
        { PermanentName: "PersonLocation", FieldType: "Contact Person Location" },
    ];

    const defaultCustomerAddressFields = [
        { PermanentName: "Address", FieldType: "Address" },
        { PermanentName: "CountryId", FieldType: "Country" },
        { PermanentName: "StateId", FieldType: "State" },
        { PermanentName: "CityId", FieldType: "City" },
        { PermanentName: "LocalityId", FieldType: "Locality" },
        { PermanentName: "PinCode", FieldType: "Pincode" },
    ];

    const defaultMoreInfoFields = [
        { PermanentName: "LeadSourceId", FieldType: "Customer Source" },
        { PermanentName: "LeadStageId", FieldType: "Customer Stage" },
        { PermanentName: "Website", FieldType: "Website (Optional)" },
        { PermanentName: "LeadTags", FieldType: "Tags" },
        { PermanentName: "OtherInfo", FieldType: "Other Information (Optional)" },
        { PermanentName: "TerritoryId", FieldType: "Territory" },
        { PermanentName: "FrontView", FieldType: "Attach Front Visiting Card" },
        { PermanentName: "BackView", FieldType: "Attach Back Visiting Card" },
        { PermanentName: "IsVisibleToAll", FieldType: "Make Visible To All Team Members" },
    ];

    const defaultPrimarySecondarySalesInfo = [
        { PermanentName: "LeadTypeId", FieldType: "Customer Type" },
        { PermanentName: "ParentLeadId", FieldType: "Customer Parent Name" },
    ];

    const FieldMapping: Record<string, string> = {
        "Customer Name": "input",
        "Ref No": "input",
        "Referred By": "select",
        "Contact Person Name": "input",
        "Mobile No": "input",
        "Telephone No": "input",
        "Email": "input",
        "Contact Person Designation": "autocomplete",
        "Contact Person Location": "input",
        "Address": "input",
        "Country": "select",
        "State": "select",
        "City": "select",
        "Locality": "select",
        "Pincode": "input",
        "Customer Source": "select",
        "Customer Stage": "select",
        "Territory": "select",
        "Website (Optional)": "input",
        "Other Information (Optional)": "input",
        "Attach Front Visiting Card": "file",
        "Attach Back Visiting Card": "file",
        "Tags": "multiAssign",
        "Make Visible To All Team Members": "checkbox",
        "Customer Type": "select",
        "Customer Parent Name": "select",
    };

    const CustomFieldMapping: Record<string, string> = {
        Text: "input",
        Multiple: "select",
        Single: "select",
        Quantitative: "input",
        Image: "file",
        CameraImage: "file",
        Signature: "file",
        Date: "date",
        YesNo: "radio"
    }

    const loadDropdown = async (field: string, parentId?: any) => {
        let res: any[] = [];

        switch (field) {
            case "CountryId":
                res = countryList || [];
                break;
            case "StateId":
                parentId && (res = (await UserService.getStateList(parentId)).data || []);
                break;
            case "CityId":
                parentId && (res = (await UserService.getCityList(parentId)).data || []);
                break;
            case "LocalityId":
                parentId &&
                    (res = (await UserService.getLocalityList(parentId)).data || []);
                break;
            case "LeadSourceId":
                res = sourceList || [];
                break;
            case "LeadStageId":
                res = stageList || [];
                break;
            case "TerritoryId":
                res = territoryList || [];
                break;
            case "LeadTypeId":
                res = typeList || [];
                break;
            case "ReferredBy":
                res = referralList || [];
                break;
            case "ParentLeadId":
                if (!parentId) break;
                const r = await CustomerService.getLeadbyNextLeadType({
                    from: 1,
                    to: -1,
                    LeadTypeId: parentId,
                });
                res = r?.data?.Leads || [];
                break;
            case "PersonDesignation":
                res = leadDesignation || [];
                break;
        }

        const map: any = {
            CountryId: ["CountryId", "Name"],
            StateId: ["StateId", "Name"],
            CityId: ["CityId", "Name"],
            LocalityId: ["LocalityId", "LocalityName"],
            LeadSourceId: ["Id", "Source"],
            LeadStageId: ["Id", "Name"],
            TerritoryId: ["TerritoryId", "TerritoryName"],
            LeadTypeId: ["Id", "TypeName"],
            ReferredBy: ["referralUserId", "userName"],
            ParentLeadId: ["Id", "LeadName"],
            PersonDesignation: ["Id", "Title"],
        };

        if (!map[field]) return;

        setSelectOptions((p) => ({
            ...p,
            [field]: res.map((o) => ({
                value: o[map[field][0]],
                label: o[map[field][1]],
            })),
        }));
    };

    const country = watch("CountryId");
    const state = watch("StateId");
    const city = watch("CityId");
    const parentId = watch("LeadTypeId");

    const capitalizeFirstLetter = (value?: string) => value ? value.trim().replace(/\b\w/g, (char) => char.toUpperCase()) : "";

    useEffect(() => {
        if (countryList) loadDropdown("CountryId");
        if (sourceList) loadDropdown("LeadSourceId");
        if (stageList) loadDropdown("LeadStageId");
        if (territoryList) loadDropdown("TerritoryId");
        if (typeList) loadDropdown("LeadTypeId");
        if (referralList) loadDropdown("ReferredBy");
        if (leadDesignation) loadDropdown("PersonDesignation");
    }, [
        countryList,
        sourceList,
        stageList,
        territoryList,
        typeList,
        referralList,
        leadDesignation,
    ]);

    useEffect(() => {
        country && loadDropdown("StateId", country);
    }, [country]);

    useEffect(() => {
        state && loadDropdown("CityId", state);
    }, [state]);

    useEffect(() => {
        city && loadDropdown("LocalityId", city);
    }, [city]);

    useEffect(() => {
        parentId && loadDropdown("ParentLeadId", parentId);
    }, [parentId]);

    useEffect(() => {
        if (!open) {
            isEditInitialized.current = false;
            setCustomFieldFiles({});
            return;
        }

        if (action === "add" || !data) return;

        if (isFetchingFields) return;

        if (leadDesignation && leadDesignation.length > 0 && !selectOptions?.PersonDesignation) return;

        if (isEditInitialized.current) return;

        const designationObj = selectOptions?.PersonDesignation?.find(
            (item: any) =>
                item.label?.trim().toLowerCase() ===
                (data?.PersonDesignation || "").trim().toLowerCase()
        );

        const customFieldDefaults: any = {};
        const customFileNames: Record<string, string> = {};

        if (data?.CustomFields && customFields) {
            customFields.forEach((f: any) => {
                const selected = data.CustomFields.find((cf: any) => cf.CustomFieldId === f.Id);
                if (selected) {
                    if (f.FieldType === "Single" || f.FieldType === "YesNo") {
                        customFieldDefaults[f.Placeholder] = selected.OptionId?.[0] ?? null;
                    } else if (f.FieldType === "Multiple") {
                        customFieldDefaults[f.Placeholder] = selected.OptionId || [];
                    } else {
                        customFieldDefaults[f.Placeholder] = selected.CustomFieldValue || "";
                    }
                }

                if (
                    ["Image", "CameraImage", "Signature"].includes(f.FieldType)
                ) {
                    customFileNames[f.Placeholder] =
                        selected?.CustomFieldValue
                            ? extractFileInfo(selected.CustomFieldValue)?.name ||
                            selected.CustomFieldValue.split("/").pop() ||
                            ""
                            : "";
                }
            });

            setCustomFieldFiles(customFileNames);
            setInitialCustomFieldFiles(customFileNames);
        }

        setFileNames({
            FrontView:
                extractFileInfo(data?.FrontView)?.name ||
                data?.FrontView?.split("/").pop() ||
                "",
            BackView:
                extractFileInfo(data?.BackView)?.name ||
                data?.BackView?.split("/").pop() ||
                "",
        });

        setRemovedFiles({
            RemoveFrontView: false,
            RemoveBackView: false,
        });
        const mobile = splitDialCode(data.MobileNo);
        
        const payload = {
            LeadName: data.LeadName ?? "",
            RefNo: data.RefNo ?? "",
            ReferredBy: normalize(data.ReferredById),
            PersonName: data.PersonName ?? "",
            // MobileNo: data.MobileNo ?? "",
            // ContactNo: data.ContactNo ?? "",
            MobileNo:  mobile.number ?? "",
            MobileNoCountryCode: mobile.dial, 
            ContactNo:data.ContactNo ?? "",
            Email: data.Email ?? "",
            PersonDesignation: designationObj ? designationObj.value : (data?.PersonDesignation || ""),
            PersonLocation: capitalizeFirstLetter(data.PersonLocation) ?? "",
            Address: data.Address ?? "",
            CountryId: normalize(data.CountryId),
            StateId: normalize(data.StateId),
            CityId: normalize(data.CityId),
            LocalityId: normalize(data.LocalityId),
            PinCode: data.PinCode ?? "",
            LeadSourceId: normalize(data.LeadSourceId),
            LeadStageId: normalize(data.LeadStageId),
            TerritoryId: normalize(data.TerritoryId),
            Website: data.Website ?? "",
            OtherInfo: data.OtherInfo ?? "",
            IsVisibleToAll: data.IsVisibleToAll ?? false,
            LeadTypeId: normalize(data.LeadTypeId),
            ParentLeadId: normalize(data.ParentLeadId),
            LeadTags: data.LeadTags ?? [],
            FrontView: data?.FrontView || "",
            BackView: data?.BackView || "",
            Latitude: data?.Latitude || 0,
            Longitude: data?.Longitude || 0,
            // LeadContacts: data?.LeadContacts?.filter((contact: any) => contact?.IsActive) || [],
            LeadContacts: (data?.LeadContacts?.filter((c: any) => c?.IsActive) || []).map((c: any) => {
                    const split = splitDialCode(c.ContactNo);
                    return { ...c, ContactNo: split.number, ContactNoCountryCode: split.dial };
                }),
            ...customFieldDefaults
        };

        setTimeout(() => {
            reset(payload);
        }, 50);

        isEditInitialized.current = true;
    }, [action, data, selectOptions, reset, open, customFields, leadDesignation, isFetchingFields]);

    const handleFormSubmit = async (values: any) => {
        const CustomFieldValues = await Promise.all(
            (customFields || [])?.map(async (f: any) => {
                const v = values[f.Placeholder];
                let optionId: number[] = [];
                let customFieldValue = "";

                if (f.FieldType === "Single" || f.FieldType === "YesNo") {
                    optionId = v != null ? [v] : [];
                } else if (f.FieldType === "Multiple") {
                    optionId = Array.isArray(v) ? v : [];
                } else if (f.FieldType === "Text" || f.FieldType === "Quantitative" || f.FieldType === "Date") {
                    customFieldValue = v != null ? String(v) : "";
                } else if (f.FieldType === "Image" || f.FieldType === "CameraImage" || f.FieldType === "Signature") {
                    if (v instanceof File) {
                        customFieldValue = await fileToBase64(v);
                    } else if (typeof v === "string" && v.trim() !== "") {
                        customFieldValue = v;
                    } else {
                        customFieldValue = "";
                    }
                }

                return {
                    CustomFieldId: f.Id,
                    FieldType: f.FieldType,
                    Placeholder: f.Placeholder,
                    Value: customFieldValue,
                    OptionId: optionId,
                    Options: f.Options || [],
                    IsMandatory: f.IsMandatory,
                    KYCPoints: f.KYCPoints ?? null,
                };
            })
        );

        let personDesignationName = "";

        if (typeof values?.PersonDesignation === "string") {
            personDesignationName = values.PersonDesignation;
        } else {
            const desigOption = selectOptions?.PersonDesignation?.find(
                (opt: any) => opt.value === values.PersonDesignation
            );

            if (desigOption) {
                personDesignationName = desigOption.label;
            }
        }

        let leadTagsPayload = values?.LeadTags || [];

        if (action === "edit" && data?.LeadTags) {
            const oldTags = data.LeadTags || [];
            const newTags = values?.LeadTags || [];

            const removedTags = oldTags
                .filter(
                    (oldTag: any) =>
                        !newTags.some((newTag: any) => newTag.Id === oldTag.Id)
                )
                .map((tag: any) => ({
                    Id: tag.Id,
                    TagName: tag.TagName,
                    IsActive: false,
                    IsRemoved: true,
                }));

            const selectedTags = newTags.map((tag: any) => ({
                Id: tag.Id,
                TagName: tag.TagName,
            }));

            leadTagsPayload = [...selectedTags, ...removedTags];
        }

        // const activeContacts = (values?.LeadContacts || []).map(
        //     (contact: any) => {
        //         const { id, ...rest } = contact;

        //         // Existing contact
        //         if (rest.Id) {
        //             return {
        //                 ...rest,
        //                 IsActive: true,
        //             };
        //         }

        //         // New contact
        //         return {
        //             Name: contact.Name,
        //             ContactNo: contact.ContactNo,
        //             Email: contact.Email,
        //             Designation: contact.Designation,
        //             Location: capitalizeFirstLetter(contact.Location)
        //         };
        //     }
        // );

        const activeContacts = (values?.LeadContacts || []).map(
            (contact: any) => {
                const { id, ContactNoCountryCode, ...rest } = contact;
                const combinedContactNo = combineDialCode(ContactNoCountryCode, contact.ContactNo);


                // Existing contact
                if (rest.Id) {
                    return {
                        ...rest,
                        ContactNo: combinedContactNo,
                        IsActive: true,
                    };
                }

                // New contact
                return {
                    Name: contact.Name,
                    ContactNo: combinedContactNo,
                    Email: contact.Email,
                    Designation: contact.Designation,
                    Location: capitalizeFirstLetter(contact.Location)
                };
            }
        );
        const leadContactsPayload = [
            ...activeContacts,
            ...removedContacts,
        ];

        let FrontView = "";
        let BackView = "";

        let RemoveFrontView;
        let RemoveBackView;

        if (values.FrontView instanceof File) {
            FrontView = await fileToBase64(values.FrontView);
            RemoveFrontView = false;
        } else if (removedFiles.RemoveFrontView) {
            FrontView = "";
            RemoveFrontView = true;
        }

        if (values.BackView instanceof File) {
            BackView = await fileToBase64(values.BackView);
            RemoveBackView = false;
        } else if (removedFiles.RemoveBackView) {
            BackView = "";
            RemoveBackView = true;
        }

        const payload: any = {
            LeadId: data?.Id || 0,
            IsVisibleToAll: values?.IsVisibleToAll || false,
            LeadName: values?.LeadName || "",
            RefNo: values?.RefNo || "",
            ReferredBy: values?.ReferredBy || 0,
            PersonName: values?.PersonName || "",
            // MobileNo: values?.MobileNo || "",
            MobileNo: combineDialCode(values?.MobileNoCountryCode, values?.MobileNo),
            ContactNo: values?.ContactNo || "",
            Email: values?.Email || "",
            PersonDesignation: personDesignationName || "",
            PersonLocation: capitalizeFirstLetter(values?.PersonLocation) ?? "",
            Address: values?.Address || "",
            CountryId: values?.CountryId || 0,
            StateId: values?.StateId || 0,
            CityId: values?.CityId || 0,
            LocalityId: values?.LocalityId || 0,
            PinCode: values?.PinCode || "",
            LeadSourceId: values?.LeadSourceId || 0,
            LeadStageId: values?.LeadStageId || 0,
            TerritoryId: values?.TerritoryId || 0,
            Website: values?.Website || "",
            OtherInfo: values?.OtherInfo || "",
            LeadTypeId: values?.LeadTypeId || 0,
            ParentLeadId: values?.ParentLeadId || 0,
            FrontView,
            BackView,
            CustomFieldValues: CustomFieldValues,
            LeadTags: leadTagsPayload,
            Longitude: values?.Longitude || 0,
            Latitude: values?.Latitude || 0,
            LeadContacts: leadContactsPayload,
        };

        if (RemoveFrontView !== undefined) {
            payload.RemoveFrontView = RemoveFrontView;
        }

        if (RemoveBackView !== undefined) {
            payload.RemoveBackView = RemoveBackView;
        }

        try {
            await onSubmit(payload);
            if (!data) {
                reset({});
            }
            closeModal();
        } catch (err) {
            toast.error(err as string || "Something went wrong");
        }
    };

    const getFieldsBySource = (source: string) => {
        if (hasApiFields) {
            return customerCustomFields.CustomFields.filter(
                (f: any) => f.SourceName === source
            );
        }

        if (source === "customerInfo") return defaultCustomerInfoFields;
        if (source === "primaryContactFields") return defaultPrimaryContactFields;
        if (source === "customerAddressFields") return defaultCustomerAddressFields;
        if (source === "moreInfoFields") return defaultMoreInfoFields;
        if (source === "primarySecondarySalesInfo") return defaultPrimarySecondarySalesInfo;

        return [];
    };

    const renderFields = (source: string) =>
        getFieldsBySource(source).map((field: any) => {
            const fieldValue = watchedValues?.[field.PermanentName];

            const showStar =
                field.KYCPoints > 0 && !fieldValue;

            const showTick =
                field.KYCPoints > 0 && !!fieldValue;

            const controlType = FieldMapping[field.FieldType] ?? "input";
            let options: any[] | undefined;

            const isLeadNameDefaultRequired = !hasApiFields && field.PermanentName === "LeadName";

            if (controlType === "select" || controlType === "autocomplete")
                options = selectOptions[field.PermanentName] || [];

            if (controlType === "multiAssign") options = customerTags || [];

            if (field.PermanentName === "MobileNo") {
                return (
                    <Box mb={0.5} key={field.PermanentName}>
                        <PhoneNumberInput
                            control={control}
                            trigger={trigger}
                            numberFieldName={field.PermanentName}
                            dialCodeFieldName={`${field.PermanentName}CountryCode`}
                            label={field.FieldType}
                            required={field.IsMandatory}
                            maxLength={MaxMobileNumberLength}
                            countries={countryList || []}
                            defaultDialCode="+91"
                            endAdornment={getKycAdornment(showStar, showTick, field.KYCPoints)}
                        />
                    </Box>
                );
            }

            if (field.PermanentName === "Address") {
                return (
                    <Box
                        mb={0.5}
                        key={field.PermanentName}
                        sx={{ gridColumn: field.PermanentName === "Address" ? "1 / -1" : "auto" }}
                    >
                        <FormControl
                            control={control}
                            controlType="input"
                            label="Address"
                            name="Address"
                            size="small"
                            sx={{ "& .MuiInputLabel-root": { fontSize: "13px" }, "& .MuiInput-root": { fontSize: "14px" } }}
                            required={field.IsMandatory}
                            rules={{
                                required: field.IsMandatory
                                    ? `${field.FieldType} is required`
                                    : false,
                            }}
                            slotProps={{
                                input: {
                                    endAdornment: getKycAdornment(
                                        showStar,
                                        showTick,
                                        field.KYCPoints
                                    ),
                                },
                                htmlInput: {
                                    readOnly: true,
                                    placeholder: "Click to select address",
                                    onClick: () => handleOpenMap(),
                                },
                            }}
                        />
                    </Box>
                );
            }

            return (
                <Box
                    mb={0.5}
                    mt={controlType === "checkbox" ? 1 : 0}
                    key={field.PermanentName}
                >   
                    <FormControl
                        key={field.PermanentName}
                        control={control}
                        controlType={controlType}
                        label={field.FieldType}
                        name={field.PermanentName}
                        size="small"
                        sx={{ "& .MuiInputLabel-root": { fontSize: "13px" }, "& .MuiInput-root": { fontSize: "14px" } }}
                        options={options}
                        required={field.IsMandatory}
                        // maxLength={field.PermanentName === "MobileNo" ? MaxMobileNumberLength : field.PermanentName === "LeadName" ? 150 : undefined}
                        maxLength={field.PermanentName === "ContactNo" ? MaxMobileNumberLength : field.PermanentName === "LeadName" ? 150 : undefined}

                        {...(field.PermanentName === "PersonDesignation" && {
                            freeSolo: true,
                            idKey: "value",
                            nameKey: "label",
                        })}

                        {...(controlType === "multiAssign" && {
                            idKey: "Id",
                            nameKey: "TagName",
                            type: "Tag"
                        })}

                        {...(controlType === "input" && {
                            numbersOnly:
                                field.PermanentName === "ContactNo",

                            alphabetsOnly:
                                field.PermanentName === "PersonName",

                            allowCommonSpecialChars:
                                field.PermanentName === "LeadName",
                        })}

                        {...(controlType === "file" && {
                            accept: "image/*",
                            showAttachmentIcon: true,
                            fileName: fileNames[field.PermanentName as keyof typeof fileNames] || "",
                            maxSize: 5 * 1024 * 1024,
                            onFileChange: (file: File | null) => {
                                if (field.PermanentName === "FrontView") {
                                    setRemovedFiles(prev => ({
                                        ...prev,
                                        RemoveFrontView: !file
                                    }));
                                }

                                if (field.PermanentName === "BackView") {
                                    setRemovedFiles(prev => ({
                                        ...prev,
                                        RemoveBackView: !file
                                    }));
                                }

                                if (
                                    field.PermanentName === "FrontView" ||
                                    field.PermanentName === "BackView"
                                ) {
                                    setFileNames((prev) => ({
                                        ...prev,
                                        [field.PermanentName]: file?.name || "",
                                    }));
                                }
                            }
                        })}

                        rules={{
                            required: (field.IsMandatory || isLeadNameDefaultRequired)
                                ? `${field.FieldType} is required`
                                : false,

                            ...(field.PermanentName === "Email" && {
                                pattern: {
                                    value: emailRegex,
                                    message: "Please enter a valid email"
                                },
                            }),

                            ...(( field.PermanentName === "ContactNo") && {
                                pattern: {
                                    value: new RegExp(`^[0-9]{${MinMobileNumberLength},${MaxMobileNumberLength}}$`),
                                    message: MinMobileNumberLength === MaxMobileNumberLength
                                        ? `${field.FieldType} must be ${MinMobileNumberLength} digits only`
                                        : `${field.FieldType} must be ${MinMobileNumberLength}-${MaxMobileNumberLength} digits only`,
                                },
                            }),
                        }}

                        slotProps={{
                            ...(controlType === "input" && {
                                input: {
                                    endAdornment: getKycAdornment(
                                        showStar,
                                        showTick,
                                        field.KYCPoints
                                    ),
                                },
                            }),
                            ...(field.PermanentName === "PersonLocation" && {
                                htmlInput: {
                                    style: {
                                        textTransform: "capitalize",
                                    },
                                    onInput: (e: any) => {
                                        e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                                    },
                                },
                            })
                        }}
                    />
                </Box>
            );
        });

    const renderCustomFields = () => customFields?.map((field: any) => {
        const fieldValue = watchedValues?.[field.Placeholder];

        const showStar =
            field.KYCPoints > 0 && !fieldValue;

        const showTick =
            field.KYCPoints > 0 && !!fieldValue;
        return (
            <Box
                mb={0.5}
                key={field.Id}
            >
                <FormControl
                    key={field.Id}
                    control={control}
                    controlType={CustomFieldMapping[field.FieldType] ?? "input"}
                    label={field.Name}
                    name={field.Placeholder}
                    size="small"
                    sx={{ "& .MuiInputLabel-root": { fontSize: "13px" }, "& .MuiInput-root": { fontSize: "14px" } }}
                    options={
                        Array.isArray(field.Options)
                            ? field.Options.map((o: any) => ({
                                value: o.Id,
                                label: o.Value,
                            }))
                            : []
                    }
                    rules={{
                        required: field.IsMandatory
                            ? `${field.FieldType} is required`
                            : false,
                    }}
                    required={field.IsMandatory}
                    slotProps={{
                        input: {
                            endAdornment: getKycAdornment(
                                showStar,
                                showTick,
                                field.KYCPoints
                            ),
                        },
                    }}

                    {...(field.FieldType === "Quantitative" && {
                        numbersOnly: true,
                    })}

                    {...(field.FieldType === "Multiple" && {
                        isMulti: true
                    })}

                    {...((field.FieldType === "Multiple" || field.FieldType === "Single") && {
                        idKey: "value",
                        nameKey: "label"
                    })}

                    {...((field.FieldType === "Image" || field.FieldType === "CameraImage" || field.FieldType === "Signature") && {
                        accept: "image/*",
                        fileName: customFieldFiles[field.Placeholder] || "",
                        showAttachmentIcon: true,
                        maxSize: 5 * 1024 * 1024,
                        onFileChange: (file: File | null) => {
                            if (!file) {
                                setCustomFieldFiles((prev) => ({
                                    ...prev,
                                    [field.Placeholder]: initialCustomFieldFiles[field.Placeholder] || "",
                                }));

                                setValue(field.Placeholder, initialCustomFieldFiles[field.Placeholder] || "");
                                return;
                            }

                            setCustomFieldFiles((prev) => ({
                                ...prev,
                                [field.Placeholder]: file.name,
                            }));

                            setValue(field.Placeholder, file);
                        }
                    })}
                />
            </Box>
        );
    });

    const hasSource = (source: string): boolean => {
        if (hasApiFields) {
            return customerCustomFields.CustomFields.some(
                (f: any) => f.SourceName === source
            );
        }

        return true;
    };

    const handleCloseModal = () => {
        reset();
        setRemovedContacts([]);

        setFileNames({
            FrontView: "",
            BackView: "",
        });
        isEditInitialized.current = false;
        closeModal();
    };

    const hasApiFields = Array.isArray(customerCustomFields?.CustomFields) && customerCustomFields.CustomFields.length > 0;

    return (
        <>
            <Modal
                modalOpen={open}
                closeModal={handleCloseModal}
                heading={data ? "Edit Customer" : "New Customer"}
                width="800px"
                onSave={handleSubmit(handleFormSubmit)}
                // isDisabled={!isValid || isSubmitting}
                isLoading={isSubmitting}
            >
                <form>
                    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
                        {renderFields("customerInfo")}
                    </Box>

                    {
                        hasSource("primaryContactFields") &&
                        <>
                            <Typography sx={{ mt: 2, fontWeight: 600, fontSize: "13px", mb: 0.5 }}>
                                Primary Contact
                            </Typography>
                            <Divider sx={{ borderStyle: "dashed" }} />
                        </>
                    }

                    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mt={1}>
                        {renderFields("primaryContactFields")}
                        {
                            hasSource("primaryContactFields") &&
                            <>
                                <Box>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() =>
                                            append({
                                                Name: "",
                                                ContactNo: "",
                                                Email: "",
                                                Designation: "",
                                                Location: ""
                                            })
                                        }
                                    >
                                        Add More
                                    </Button>
                                </Box>
                            </>
                        }
                    </Box>

                    {alternateContacts.map((item, index) => (
                        <Box key={item.id} mt={4}>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography fontWeight={600}>
                                    Alternate Contact {index + 1}
                                </Typography>

                                <Button
                                    sx={{ fontSize: "12px", padding: "4px 12px", backgroundColor: "#fff", color: "#000000de" }}
                                    variant="contained"
                                    size="small"
                                    onClick={() => {
                                        const contact = watch(`LeadContacts.${index}`) as LeadContact;

                                        if (contact?.Id) {
                                            const { id, ...rest } = contact;

                                            setRemovedContacts(prev => [
                                                ...prev,
                                                {
                                                    ...rest,
                                                    IsActive: false,
                                                },
                                            ]);
                                        }

                                        remove(index);
                                    }}
                                >
                                    Remove
                                </Button>
                            </Box>

                            <Divider sx={{ my: 1, borderStyle: "dashed" }} />

                            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
                                <FormControl
                                    control={control}
                                    name={`LeadContacts.${index}.Name`}
                                    label="Contact Person Name"
                                    controlType="input"
                                    size="small"
                                    sx={{ "& .MuiInputLabel-root": { fontSize: "13px" }, "& .MuiInput-root": { fontSize: "14px" } }}
                                    alphabetsOnly
                                />

                                {/* <FormControl
                                    control={control}
                                    name={`LeadContacts.${index}.ContactNo`}
                                    label="Contact Number"
                                    controlType="input"
                                    size="small"
                                    sx={{ "& .MuiInputLabel-root": { fontSize: "13px" }, "& .MuiInput-root": { fontSize: "14px" } }}
                                    maxLength={MaxMobileNumberLength}
                                    rules={{
                                        pattern: {
                                            value: new RegExp(`^[0-9]{${MinMobileNumberLength},${MaxMobileNumberLength}}$`),
                                            message: MinMobileNumberLength === MaxMobileNumberLength
                                                ? `Contact number must be ${MinMobileNumberLength} digits only`
                                                : `Contact number must be ${MinMobileNumberLength}-${MaxMobileNumberLength} digits only`,
                                        },
                                    }}
                                    numbersOnly
                                /> */}
                                <PhoneNumberInput
                                    control={control}
                                    trigger={trigger}
                                    numberFieldName={`LeadContacts.${index}.ContactNo`}
                                    dialCodeFieldName={`LeadContacts.${index}.ContactNoCountryCode`}
                                    label="Contact Number"
                                    maxLength={MaxMobileNumberLength}
                                    countries={countryList || []}
                                    defaultDialCode="+91"
                                />

                                <FormControl
                                    control={control}
                                    name={`LeadContacts.${index}.Email`}
                                    label="Email"
                                    controlType="input"
                                    size="small"
                                    sx={{ "& .MuiInputLabel-root": { fontSize: "13px" }, "& .MuiInput-root": { fontSize: "14px" } }}
                                    rules={{
                                        pattern: {
                                            value: emailRegex,
                                            message: "Please enter a valid email",
                                        },
                                    }}
                                />
                                <FormControl
                                    control={control}
                                    name={`LeadContacts.${index}.Designation`}
                                    label="Contact Person Designation"
                                    controlType="input"
                                    size="small"
                                    sx={{ "& .MuiInputLabel-root": { fontSize: "13px" }, "& .MuiInput-root": { fontSize: "14px" } }}
                                />
                                <FormControl
                                    control={control}
                                    name={`LeadContacts.${index}.Location`}
                                    label="Contact Person Location"
                                    alphabetsOnly
                                    controlType="input"
                                    size="small"
                                    rules={{
                                        maxLength: {
                                            value: 50,
                                            message: "Maximum 50 characters is allowed"
                                        }
                                    }}
                                    slotProps={{
                                        htmlInput: {
                                            style: {
                                                textTransform: "capitalize",
                                            },
                                        },
                                    }}
                                    sx={{ "& .MuiInputLabel-root": { fontSize: "13px" }, "& .MuiInput-root": { fontSize: "14px" } }}
                                />
                            </Box>
                        </Box>
                    ))}

                    {
                        hasSource("customerAddressFields") &&
                        <>
                            <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "13px", mb: 0.5 }}>Customer Address</Typography>
                            <Divider sx={{ borderStyle: "dashed" }} />
                        </>
                    }
                    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mt={1}>
                        {renderFields("customerAddressFields")}
                    </Box>

                    {
                        hasSource("moreInfoFields") &&
                        <>
                            <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "13px", mb: 0.5 }}>More Info</Typography>
                            <Divider sx={{ borderStyle: "dashed" }} />
                        </>
                    }
                    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mt={1}>
                        {renderFields("moreInfoFields")}
                    </Box>

                    {
                        hasSource("primarySecondarySalesInfo") &&
                        <>
                            <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "13px", mb: 0.5 }}>Primary-Secondary Sales Info</Typography>
                            <Divider sx={{ borderStyle: "dashed" }} />
                        </>
                    }
                    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mt={1}>
                        {renderFields("primarySecondarySalesInfo")}
                    </Box>

                    {
                        customFields?.length > 0 &&
                        <>
                            <Typography sx={{ mt: 3, fontWeight: 600, fontSize: "13px", mb: 0.5 }}>Custom Fields</Typography>
                            <Divider sx={{ borderStyle: "dashed" }} />
                            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mt={1}>
                                {renderCustomFields()}
                            </Box>
                        </>
                    }
                </form>
            </Modal>

            <AddressDialog
                modalOpen={openMap}
                closeModal={() => setOpenMap(false)}
                title="Select Address"
                searchable
                lat={watch("Latitude")}
                lng={watch("Longitude")}
                onDone={(data) => {
                    setValue("Latitude", data.lat);
                    setValue("Longitude", data.lng);
                    setValue("Address", data.address);
                }}
            />
        </>
    );
};

export default memo(NewCustomerModal);
